import { Fragment, type ReactNode } from "react";

/**
 * Deliberately tiny Markdown subset for policy pages: #/##/### headings,
 * paragraphs, "- " lists, "> " quotes, **bold**. No raw HTML ever.
 */
function inline(text: string, key: number): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <Fragment key={key}>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <strong key={i}>{p.slice(2, -2)}</strong>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </Fragment>
  );
}

function takeWhile(
  lines: string[],
  start: number,
  pred: (l: string) => boolean,
): { items: string[]; next: number } {
  const items: string[] = [];
  let i = start;
  while (i < lines.length && pred(lines[i] ?? "")) {
    items.push(lines[i] ?? "");
    i++;
  }
  return { items, next: i };
}

const isBlockStart = (l: string) => /^(#{1,3}\s|- |> )/.test(l);

export function Markdown({ source }: { source: string }) {
  const out: ReactNode[] = [];
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      i++;
      continue;
    }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length;
      const text = inline(h[2]!, 0);
      key++;
      out.push(
        level === 1 ? (
          <h1 key={key} className="h1">
            {text}
          </h1>
        ) : level === 2 ? (
          <h2 key={key} className="h2">
            {text}
          </h2>
        ) : (
          <h3 key={key}>{text}</h3>
        ),
      );
      i++;
      continue;
    }
    if (line.startsWith("- ")) {
      const { items, next } = takeWhile(lines, i, (l) => l.startsWith("- "));
      out.push(
        <ul key={key++}>
          {items.map((it, j) => (
            <li key={j}>{inline(it.slice(2), j)}</li>
          ))}
        </ul>,
      );
      i = next;
      continue;
    }
    if (line.startsWith("> ")) {
      const { items, next } = takeWhile(lines, i, (l) => l.startsWith("> "));
      out.push(
        <blockquote key={key++} className="quote">
          {inline(items.map((q) => q.slice(2)).join(" "), 0)}
        </blockquote>,
      );
      i = next;
      continue;
    }
    const { items, next } = takeWhile(lines, i, (l) => l.trim() !== "" && !isBlockStart(l));
    out.push(<p key={key++}>{inline(items.join(" "), 0)}</p>);
    i = next;
  }
  return <div className="prose">{out}</div>;
}
